import {
  FileEdit,
  Calculator,
  Cloud,
  Headphones,
  BarChart3,
  FolderKanban,
  KeyRound,
  Package,
  Percent,
  Ship,
  type LucideIcon,
} from 'lucide-react';

export interface TemplateLookupTable {
  code: string;
  name: string;
  rowDimension: string;
  colDimension: string;
  data: {
    rows: string[];
    cols: string[];
    values: number[][];
  };
}

export interface TemplateVariable {
  code: string;
  name: string;
  variableType: 'NUMBER' | 'PERCENTAGE' | 'CURRENCY' | 'TEXT' | 'BOOLEAN' | 'CALCULATED' | 'LOOKUP';
  defaultValue?: string;
  formula?: string;
  description?: string;
  order: number;
  lookupTableCode?: string;
  /**
   * El papel de la variable en la cotización (costo, margen, total). Sin él, la
   * cotización tomaba el primer nombre conocido y el «GRAN TOTAL» de «Costo +
   * Margen» salía igual al costo, sin el margen.
   */
  priceRole?: 'COST_INPUT' | 'COST_BASE_MARGIN' | 'QUANTITY' | 'MARGIN_PCT' | 'TOTAL_PRICE';
}

/*
  Los porcentajes de entrada llegan a las fórmulas como fracción: el evaluador
  convierte «25» en 0,25 (`formula-evaluator.ts`, igual en el servidor). Las
  fórmulas de las plantillas dividían otra vez por 100 y un margen de 25 %
  sumaba 0,25 % (prueba de punta a punta en DEV, 28-oct). Ninguna fórmula de
  acá divide un porcentaje por 100.
*/

export interface ModelTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  icon: LucideIcon;
  targetMarginPercentage: number;
  floorMarginPercentage: number;
  variables: TemplateVariable[];
  lookupTables?: TemplateLookupTable[];
}

export const MODEL_TEMPLATES: ModelTemplate[] = [
  // 1. Custom / En Blanco
  {
    id: 'custom',
    name: 'En Blanco',
    description: 'Crea un modelo vacio y agrega variables manualmente',
    type: 'STANDARD',
    icon: FileEdit,
    targetMarginPercentage: 25,
    floorMarginPercentage: 15,
    variables: [],
  },

  // 2. Costo + Margen
  {
    id: 'cost-plus',
    name: 'Costo + Margen',
    description: 'Calcula precio sumando un margen porcentual al costo base más flete',
    type: 'COST_PLUS',
    icon: Calculator,
    targetMarginPercentage: 25,
    floorMarginPercentage: 12,
    variables: [
      {
        code: 'COSTO_UNITARIO',
        priceRole: 'COST_INPUT',
        name: 'Costo Unitario',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Costo de adquisición por unidad',
        order: 0,
      },
      {
        code: 'CANTIDAD',
        name: 'Cantidad',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Número de unidades',
        order: 1,
      },
      {
        code: 'COSTO_TOTAL',
        name: 'Costo Total',
        variableType: 'CALCULATED',
        formula: 'COSTO_UNITARIO * CANTIDAD',
        priceRole: 'COST_BASE_MARGIN',
        description: 'Costo total = unitario x cantidad',
        order: 2,
      },
      {
        code: 'MARGEN_PCT',
        name: 'Margen (%)',
        variableType: 'PERCENTAGE',
        priceRole: 'MARGIN_PCT',
        defaultValue: '25',
        description: 'Porcentaje de margen sobre el costo',
        order: 3,
      },
      {
        code: 'FLETE',
        name: 'Flete / Envío',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Costo de transporte o envío',
        order: 4,
      },
      {
        code: 'DESCUENTO_PCT',
        name: 'Descuento (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '0',
        description: 'Descuento aplicado al subtotal',
        order: 5,
      },
      {
        code: 'SUBTOTAL',
        name: 'Subtotal',
        variableType: 'CALCULATED',
        formula: 'COSTO_TOTAL * (1 + MARGEN_PCT) + FLETE',
        description: 'Costo con margen más flete',
        order: 6,
      },
      {
        code: 'TOTAL',
        name: 'Total',
        variableType: 'CALCULATED',
        formula: 'SUBTOTAL * (1 - DESCUENTO_PCT)',
        priceRole: 'TOTAL_PRICE',
        description: 'Precio final con descuento aplicado',
        order: 7,
      },
    ],
  },

  // 3. Suscripcion SaaS
  {
    id: 'subscription',
    name: 'Suscripción SaaS',
    description: 'Modelo de suscripción mensual/anual con usuarios y descuento por compromiso',
    type: 'SUBSCRIPTION',
    icon: Cloud,
    targetMarginPercentage: 35,
    floorMarginPercentage: 20,
    variables: [
      {
        code: 'PRECIO_BASE_MENSUAL',
        name: 'Precio Base Mensual',
        variableType: 'CURRENCY',
        defaultValue: '99',
        description: 'Tarifa base mensual del plan',
        order: 0,
      },
      {
        code: 'NUM_USUARIOS',
        name: 'Número de Usuarios',
        variableType: 'NUMBER',
        defaultValue: '5',
        description: 'Cantidad de licencias/usuarios',
        order: 1,
      },
      {
        code: 'PRECIO_USUARIO_ADICIONAL',
        name: 'Precio por Usuario Adicional',
        variableType: 'CURRENCY',
        defaultValue: '15',
        description: 'Costo por cada usuario extra',
        order: 2,
      },
      {
        code: 'USUARIOS_INCLUIDOS',
        name: 'Usuarios Incluidos',
        variableType: 'NUMBER',
        defaultValue: '3',
        description: 'Usuarios incluidos en el precio base',
        order: 3,
      },
      {
        code: 'MESES_CONTRATO',
        name: 'Meses de Contrato',
        variableType: 'NUMBER',
        defaultValue: '12',
        description: 'Duración del contrato en meses',
        order: 4,
      },
      {
        code: 'DESCUENTO_ANUAL_PCT',
        name: 'Descuento Anual (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '15',
        description: 'Descuento por compromiso anual',
        order: 5,
      },
      {
        code: 'COSTO_SETUP',
        name: 'Costo de Implementación',
        variableType: 'CURRENCY',
        defaultValue: '500',
        description: 'Costo único de configuración inicial',
        order: 6,
      },
      {
        code: 'ADICIONALES',
        name: 'Modulos Adicionales',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Costo de modulos o add-ons extra',
        order: 7,
      },
      {
        code: 'USUARIOS_EXTRA',
        name: 'Usuarios Extra',
        variableType: 'CALCULATED',
        formula: 'max(NUM_USUARIOS - USUARIOS_INCLUIDOS, 0)',
        description: 'Usuarios que exceden los incluidos',
        order: 8,
      },
      {
        code: 'MENSUAL_TOTAL',
        name: 'Mensual Total',
        variableType: 'CALCULATED',
        formula: 'PRECIO_BASE_MENSUAL + (USUARIOS_EXTRA * PRECIO_USUARIO_ADICIONAL) + ADICIONALES',
        description: 'Costo mensual antes de descuento',
        order: 9,
      },
      {
        code: 'DESCUENTO_APLICADO',
        name: 'Descuento Aplicado',
        variableType: 'CALCULATED',
        formula: 'MESES_CONTRATO >= 12 ? DESCUENTO_ANUAL_PCT : 0',
        description: 'Descuento si el contrato es anual o mayor',
        order: 10,
      },
      {
        code: 'TOTAL',
        name: 'Total del Contrato',
        variableType: 'CALCULATED',
        formula: '(MENSUAL_TOTAL * MESES_CONTRATO * (1 - DESCUENTO_APLICADO)) + COSTO_SETUP',
        description: 'Valor total del contrato con descuento e implementación',
        order: 11,
      },
    ],
  },

  // 4. Servicio Gestionado
  {
    id: 'managed-service',
    name: 'Servicio Gestionado',
    description: 'Tarifa mensual por servicio gestionado con horas incluidas y excedentes',
    type: 'MANAGED_SERVICE',
    icon: Headphones,
    targetMarginPercentage: 30,
    floorMarginPercentage: 18,
    variables: [
      {
        code: 'TARIFA_BASE',
        name: 'Tarifa Base Mensual',
        variableType: 'CURRENCY',
        defaultValue: '2000',
        description: 'Fee mensual fijo del servicio',
        order: 0,
      },
      {
        code: 'HORAS_INCLUIDAS',
        name: 'Horas Incluidas',
        variableType: 'NUMBER',
        defaultValue: '40',
        description: 'Horas de servicio incluidas por mes',
        order: 1,
      },
      {
        code: 'HORAS_CONSUMIDAS',
        name: 'Horas Consumidas',
        variableType: 'NUMBER',
        defaultValue: '40',
        description: 'Horas realmente utilizadas en el mes',
        order: 2,
      },
      {
        code: 'TARIFA_HORA_EXTRA',
        name: 'Tarifa Hora Extra',
        variableType: 'CURRENCY',
        defaultValue: '75',
        description: 'Costo por hora que excede las incluidas',
        order: 3,
      },
      {
        code: 'SLA_PREMIUM',
        name: 'SLA Premium',
        variableType: 'BOOLEAN',
        defaultValue: '0',
        description: 'Activar SLA premium (soporte 24/7)',
        order: 4,
      },
      {
        code: 'RECARGO_SLA',
        name: 'Recargo SLA Premium',
        variableType: 'PERCENTAGE',
        defaultValue: '20',
        description: 'Porcentaje adicional por SLA premium',
        order: 5,
      },
      {
        code: 'NUM_SERVIDORES',
        name: 'Servidores/Activos',
        variableType: 'NUMBER',
        defaultValue: '5',
        description: 'Cantidad de servidores o activos gestionados',
        order: 6,
      },
      {
        code: 'COSTO_POR_SERVIDOR',
        name: 'Costo por Servidor',
        variableType: 'CURRENCY',
        defaultValue: '50',
        description: 'Costo mensual por servidor gestionado',
        order: 7,
      },
      {
        code: 'HORAS_EXTRA',
        name: 'Horas Extra',
        variableType: 'CALCULATED',
        formula: 'max(HORAS_CONSUMIDAS - HORAS_INCLUIDAS, 0)',
        description: 'Horas que exceden las incluidas',
        order: 8,
      },
      {
        code: 'TOTAL',
        name: 'Total Mensual',
        variableType: 'CALCULATED',
        formula:
          '(TARIFA_BASE + (NUM_SERVIDORES * COSTO_POR_SERVIDOR) + (HORAS_EXTRA * TARIFA_HORA_EXTRA)) * (1 + SLA_PREMIUM * RECARGO_SLA)',
        description: 'Total mensual incluyendo extras y SLA',
        order: 9,
      },
    ],
  },

  // 5. Volumen Escalonado
  {
    id: 'tiered-volume',
    name: 'Volumen Escalonado',
    description: 'Precio unitario que disminuye según el volumen de compra',
    type: 'TIERED_VOLUME',
    icon: BarChart3,
    targetMarginPercentage: 20,
    floorMarginPercentage: 10,
    variables: [
      {
        code: 'PRECIO_LISTA',
        name: 'Precio de Lista',
        variableType: 'CURRENCY',
        defaultValue: '100',
        description: 'Precio unitario base sin descuento',
        order: 0,
      },
      {
        code: 'CANTIDAD',
        name: 'Cantidad',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Número de unidades a cotizar',
        order: 1,
      },
      {
        code: 'DESCUENTO_VOLUMEN_PCT',
        name: 'Descuento por Volumen (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '0',
        description: 'Descuento según escala de volumen (ajustar manualmente o usar tiers)',
        order: 2,
      },
      {
        code: 'DESCUENTO_ADICIONAL_PCT',
        name: 'Descuento Adicional (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '0',
        description: 'Descuento comercial extra sobre el precio con volumen',
        order: 3,
      },
      {
        code: 'FLETE',
        name: 'Flete / Envío',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Costo de transporte',
        order: 4,
      },
      {
        code: 'PRECIO_UNITARIO',
        name: 'Precio Unitario',
        variableType: 'CALCULATED',
        formula: 'PRECIO_LISTA * (1 - DESCUENTO_VOLUMEN_PCT)',
        description: 'Precio unitario con descuento por volumen',
        order: 5,
      },
      {
        code: 'TOTAL',
        name: 'Total',
        variableType: 'CALCULATED',
        formula: '(PRECIO_UNITARIO * CANTIDAD * (1 - DESCUENTO_ADICIONAL_PCT)) + FLETE',
        description: 'Total con descuentos y flete',
        order: 6,
      },
    ],
  },

  // 6. Proyecto
  {
    id: 'project-based',
    name: 'Proyecto',
    description: 'Cotización por proyecto con horas, perfiles y gastos directos',
    type: 'PROJECT_BASED',
    icon: FolderKanban,
    targetMarginPercentage: 28,
    floorMarginPercentage: 15,
    variables: [
      {
        code: 'HORAS_SENIOR',
        name: 'Horas Senior',
        variableType: 'NUMBER',
        defaultValue: '80',
        description: 'Horas estimadas de perfil senior',
        order: 0,
      },
      {
        code: 'TARIFA_SENIOR',
        name: 'Tarifa Hora Senior',
        variableType: 'CURRENCY',
        defaultValue: '120',
        description: 'Tarifa por hora del perfil senior',
        order: 1,
      },
      {
        code: 'HORAS_JUNIOR',
        name: 'Horas Junior',
        variableType: 'NUMBER',
        defaultValue: '160',
        description: 'Horas estimadas de perfil junior',
        order: 2,
      },
      {
        code: 'TARIFA_JUNIOR',
        name: 'Tarifa Hora Junior',
        variableType: 'CURRENCY',
        defaultValue: '60',
        description: 'Tarifa por hora del perfil junior',
        order: 3,
      },
      {
        code: 'GASTOS_DIRECTOS',
        name: 'Gastos Directos',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Licencias, viajes, materiales y otros gastos',
        order: 4,
      },
      {
        code: 'CONTINGENCIA_PCT',
        name: 'Contingencia (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '10',
        description: 'Porcentaje de contingencia sobre el costo base',
        order: 5,
      },
      {
        code: 'MARGEN_PCT',
        name: 'Margen (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '28',
        description: 'Margen de ganancia sobre el costo total',
        order: 6,
      },
      {
        code: 'DESCUENTO_PCT',
        name: 'Descuento (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '0',
        description: 'Descuento comercial sobre el total',
        order: 7,
      },
      {
        code: 'COSTO_RRHH',
        name: 'Costo RRHH',
        variableType: 'CALCULATED',
        formula: '(HORAS_SENIOR * TARIFA_SENIOR) + (HORAS_JUNIOR * TARIFA_JUNIOR)',
        description: 'Costo total de recursos humanos',
        order: 8,
      },
      {
        code: 'COSTO_BASE',
        name: 'Costo Base del Proyecto',
        variableType: 'CALCULATED',
        formula: '(COSTO_RRHH + GASTOS_DIRECTOS) * (1 + CONTINGENCIA_PCT)',
        description: 'Costo base con contingencia',
        order: 9,
      },
      {
        code: 'SUBTOTAL',
        name: 'Subtotal',
        variableType: 'CALCULATED',
        formula: 'COSTO_BASE * (1 + MARGEN_PCT)',
        description: 'Precio con margen aplicado',
        order: 10,
      },
      {
        code: 'TOTAL',
        name: 'Total del Proyecto',
        variableType: 'CALCULATED',
        formula: 'SUBTOTAL * (1 - DESCUENTO_PCT)',
        description: 'Total final con descuento',
        order: 11,
      },
    ],
  },

  // 7. Leasing / Alquiler
  {
    id: 'leasing',
    name: 'Leasing / Alquiler',
    description: 'Calculo de cuota de leasing o alquiler de equipos con valor residual',
    type: 'LEASING',
    icon: KeyRound,
    targetMarginPercentage: 22,
    floorMarginPercentage: 12,
    variables: [
      {
        code: 'VALOR_ACTIVO',
        name: 'Valor del Activo',
        variableType: 'CURRENCY',
        defaultValue: '50000',
        description: 'Precio de compra del equipo o activo',
        order: 0,
      },
      {
        code: 'VALOR_RESIDUAL_PCT',
        name: 'Valor Residual (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '10',
        description: 'Porcentaje del valor al final del contrato',
        order: 1,
      },
      {
        code: 'PLAZO_MESES',
        name: 'Plazo (meses)',
        variableType: 'NUMBER',
        defaultValue: '36',
        description: 'Duración del contrato de leasing en meses',
        order: 2,
      },
      {
        code: 'TASA_ANUAL_PCT',
        name: 'Tasa Anual (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '12',
        description: 'Tasa de interes anual del leasing',
        order: 3,
      },
      {
        code: 'SEGURO_MENSUAL',
        name: 'Seguro Mensual',
        variableType: 'CURRENCY',
        defaultValue: '150',
        description: 'Costo mensual del seguro del activo',
        order: 4,
      },
      {
        code: 'MANTENIMIENTO_MENSUAL',
        name: 'Mantenimiento Mensual',
        variableType: 'CURRENCY',
        defaultValue: '100',
        description: 'Costo mensual de mantenimiento incluido',
        order: 5,
      },
      {
        code: 'VALOR_RESIDUAL',
        name: 'Valor Residual',
        variableType: 'CALCULATED',
        formula: 'VALOR_ACTIVO * VALOR_RESIDUAL_PCT',
        description: 'Monto del valor residual',
        order: 6,
      },
      {
        code: 'CUOTA_BASE',
        name: 'Cuota Base',
        variableType: 'CALCULATED',
        formula: '(VALOR_ACTIVO - VALOR_RESIDUAL) / PLAZO_MESES * (1 + TASA_ANUAL_PCT / 12)',
        description: 'Cuota mensual de amortización con interes',
        order: 7,
      },
      {
        code: 'CUOTA_TOTAL',
        name: 'Cuota Mensual Total',
        variableType: 'CALCULATED',
        formula: 'CUOTA_BASE + SEGURO_MENSUAL + MANTENIMIENTO_MENSUAL',
        description: 'Cuota mensual total incluyendo seguro y mantenimiento',
        order: 8,
      },
    ],
  },

  // 8. Bundle / Paquete
  {
    id: 'bundle',
    name: 'Bundle / Paquete',
    description: 'Paquete de productos o servicios con descuento por combo',
    type: 'BUNDLE',
    icon: Package,
    targetMarginPercentage: 25,
    floorMarginPercentage: 15,
    variables: [
      {
        code: 'ITEM_1_PRECIO',
        name: 'Item 1 - Precio',
        variableType: 'CURRENCY',
        defaultValue: '500',
        description: 'Precio del primer producto/servicio',
        order: 0,
      },
      {
        code: 'ITEM_1_CANTIDAD',
        name: 'Item 1 - Cantidad',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Cantidad del primer item',
        order: 1,
      },
      {
        code: 'ITEM_2_PRECIO',
        name: 'Item 2 - Precio',
        variableType: 'CURRENCY',
        defaultValue: '300',
        description: 'Precio del segundo producto/servicio',
        order: 2,
      },
      {
        code: 'ITEM_2_CANTIDAD',
        name: 'Item 2 - Cantidad',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Cantidad del segundo item',
        order: 3,
      },
      {
        code: 'ITEM_3_PRECIO',
        name: 'Item 3 - Precio',
        variableType: 'CURRENCY',
        defaultValue: '200',
        description: 'Precio del tercer producto/servicio',
        order: 4,
      },
      {
        code: 'ITEM_3_CANTIDAD',
        name: 'Item 3 - Cantidad',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Cantidad del tercer item',
        order: 5,
      },
      {
        code: 'DESCUENTO_BUNDLE_PCT',
        name: 'Descuento Bundle (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '10',
        description: 'Descuento por comprar el paquete completo',
        order: 6,
      },
      {
        code: 'SUMA_ITEMS',
        name: 'Suma de Items',
        variableType: 'CALCULATED',
        formula:
          '(ITEM_1_PRECIO * ITEM_1_CANTIDAD) + (ITEM_2_PRECIO * ITEM_2_CANTIDAD) + (ITEM_3_PRECIO * ITEM_3_CANTIDAD)',
        description: 'Suma total de todos los items sin descuento',
        order: 7,
      },
      {
        code: 'TOTAL',
        name: 'Total Bundle',
        variableType: 'CALCULATED',
        formula: 'SUMA_ITEMS * (1 - DESCUENTO_BUNDLE_PCT)',
        description: 'Total del paquete con descuento bundle',
        order: 8,
      },
    ],
  },

  // 9. Promocional
  {
    id: 'promotional',
    name: 'Promocional',
    description: 'Precio con descuento promocional temporal sobre precio de lista',
    type: 'PROMOTIONAL',
    icon: Percent,
    targetMarginPercentage: 15,
    floorMarginPercentage: 5,
    variables: [
      {
        code: 'PRECIO_LISTA',
        name: 'Precio de Lista',
        variableType: 'CURRENCY',
        defaultValue: '1000',
        description: 'Precio regular del producto o servicio',
        order: 0,
      },
      {
        code: 'CANTIDAD',
        name: 'Cantidad',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Número de unidades',
        order: 1,
      },
      {
        code: 'DESCUENTO_PROMO_PCT',
        name: 'Descuento Promocional (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '20',
        description: 'Porcentaje de descuento de la promoción',
        order: 2,
      },
      {
        code: 'DESCUENTO_ADICIONAL_PCT',
        name: 'Descuento Adicional (%)',
        variableType: 'PERCENTAGE',
        defaultValue: '0',
        description: 'Descuento extra (ej. por volumen o fidelidad)',
        order: 3,
      },
      {
        code: 'FLETE',
        name: 'Flete / Envío',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Costo de envío o transporte',
        order: 4,
      },
      {
        code: 'PRECIO_PROMO',
        name: 'Precio Promocional',
        variableType: 'CALCULATED',
        formula: 'PRECIO_LISTA * (1 - DESCUENTO_PROMO_PCT)',
        description: 'Precio unitario con descuento promo',
        order: 5,
      },
      {
        code: 'TOTAL',
        name: 'Total',
        variableType: 'CALCULATED',
        formula: '(PRECIO_PROMO * CANTIDAD * (1 - DESCUENTO_ADICIONAL_PCT)) + FLETE',
        description: 'Total final con todos los descuentos y flete',
        order: 6,
      },
    ],
  },

  // 10. Importacion
  {
    id: 'import-costs',
    name: 'Costos de Importación',
    description:
      'Calcula costos de importar mercaderia: flete internacional, seguro, CIF, tributos aduaneros, despacho, flete interno y almacenaje. Incluye lookup tables de tarifas.',
    type: 'COST_PLUS',
    icon: Ship,
    targetMarginPercentage: 0,
    floorMarginPercentage: 0,
    lookupTables: [
      {
        code: 'TARIFAS_FLETE',
        name: 'Tarifas Flete Internacional',
        rowDimension: 'Modo de Transporte',
        colDimension: 'Tarifa',
        data: {
          rows: ['Maritimo', 'Aereo', 'Terrestre'],
          cols: ['USD/kg'],
          values: [[0.35], [4.5], [0.8]],
        },
      },
      {
        code: 'TARIFAS_FLETE_INTERNO',
        name: 'Tarifas Flete Interno (Origen x Destino)',
        rowDimension: 'Origen',
        colDimension: 'Destino',
        data: {
          rows: ['Arica', 'Iquique', 'Aeropuerto VVI', 'Aeropuerto LPB'],
          cols: ['Santa Cruz', 'La Paz', 'Cochabamba'],
          values: [
            [380, 450, 420],
            [350, 420, 400],
            [0, 180, 120],
            [180, 0, 120],
          ],
        },
      },
    ],
    variables: [
      // Inputs basicos
      {
        code: 'VALOR_FOB',
        name: 'Valor FOB $us',
        variableType: 'CURRENCY',
        defaultValue: '0',
        description: 'Valor de la mercaderia en origen (Free On Board)',
        order: 0,
      },
      {
        code: 'PESO_KG',
        name: 'Peso Total (kg)',
        variableType: 'NUMBER',
        defaultValue: '0',
        description: 'Peso total de la carga en kilogramos',
        order: 1,
      },
      {
        code: 'CANTIDAD_BULTOS',
        name: 'Cantidad de Bultos',
        variableType: 'NUMBER',
        defaultValue: '1',
        description: 'Número de bultos o unidades importadas',
        order: 2,
      },
      // Lookup: consulta de tarifa flete
      {
        code: 'TARIFA_FLETE_REF',
        name: 'Tarifa Flete (consultar tabla)',
        variableType: 'LOOKUP',
        description: 'Consulta la tabla de tarifas de flete internacional según modo de transporte',
        order: 3,
        lookupTableCode: 'TARIFAS_FLETE',
      },
      // Flete internacional
      {
        code: 'FLETE_INTL_RATE',
        name: 'Tarifa Flete Intl. $us/kg',
        variableType: 'CURRENCY',
        defaultValue: '4.50',
        description: 'Tarifa por kg (puede usar el valor de la tabla de lookup)',
        order: 4,
      },
      {
        code: 'FLETE_INTL_MINIMO',
        name: 'Flete Intl. Mínimo $us',
        variableType: 'CURRENCY',
        defaultValue: '150',
        description: 'Monto mínimo de flete internacional',
        order: 5,
      },
      {
        code: 'FLETE_INTL_BASE',
        name: 'Flete Intl. Base $us',
        variableType: 'CALCULATED',
        formula: 'FLETE_INTL_RATE * PESO_KG',
        description: 'Flete calculado por peso',
        order: 6,
      },
      {
        code: 'FLETE_INTL',
        name: 'Flete Internacional $us',
        variableType: 'CALCULATED',
        formula: 'FLETE_INTL_BASE > FLETE_INTL_MINIMO ? FLETE_INTL_BASE : FLETE_INTL_MINIMO',
        description: 'Mayor entre flete calculado y mínimo',
        order: 7,
      },
      // Seguro
      {
        code: 'SEGURO_PCT',
        name: 'Seguro Internacional %',
        variableType: 'PERCENTAGE',
        defaultValue: '0.5',
        description: 'Porcentaje de seguro sobre valor FOB',
        order: 8,
      },
      {
        code: 'SEGURO_MONTO',
        name: 'Seguro $us',
        variableType: 'CALCULATED',
        formula: 'VALOR_FOB * SEGURO_PCT',
        description: 'Monto del seguro internacional',
        order: 9,
      },
      // CIF
      {
        code: 'VALOR_CIF',
        name: 'Valor CIF $us',
        variableType: 'CALCULATED',
        formula: 'VALOR_FOB + FLETE_INTL + SEGURO_MONTO',
        description: 'Cost, Insurance & Freight',
        order: 10,
      },
      // Tributos aduaneros
      {
        code: 'GA_PCT',
        name: 'Gravamen Arancelario (GA) %',
        variableType: 'PERCENTAGE',
        defaultValue: '5',
        description: 'Arancel de importación sobre CIF',
        order: 11,
      },
      {
        code: 'GA_MONTO',
        name: 'GA $us',
        variableType: 'CALCULATED',
        formula: '(VALOR_FOB + FLETE_INTL) * GA_PCT',
        description: 'Monto del gravamen arancelario (sobre costo + flete)',
        order: 12,
      },
      {
        code: 'IVA_IMPORT_PCT',
        name: 'IVA Importación %',
        variableType: 'PERCENTAGE',
        defaultValue: '14.94',
        description: 'IVA sobre (CIF + GA)',
        order: 13,
      },
      {
        code: 'IVA_IMPORT_MONTO',
        name: 'IVA Importación $us',
        variableType: 'CALCULATED',
        formula: '(VALOR_CIF + GA_MONTO) * IVA_IMPORT_PCT',
        description: 'Monto del IVA de importación',
        order: 14,
      },
      {
        code: 'TOTAL_TRIBUTOS',
        name: 'Total Tributos $us',
        variableType: 'CALCULATED',
        formula: 'GA_MONTO + IVA_IMPORT_MONTO',
        description: 'Suma de todos los tributos aduaneros',
        order: 15,
      },
      // Despacho aduanero
      {
        code: 'HONORARIOS_AGENTE',
        name: 'Honorarios Agente Aduanero $us',
        variableType: 'CURRENCY',
        defaultValue: '250',
        description: 'Honorarios del despachante de aduanas',
        order: 16,
      },
      {
        code: 'GASTOS_PORTUARIOS',
        name: 'Gastos Portuarios $us',
        variableType: 'CURRENCY',
        defaultValue: '120',
        description: 'Gastos de puerto y handling',
        order: 17,
      },
      {
        code: 'TOTAL_DESPACHO',
        name: 'Total Despacho $us',
        variableType: 'CALCULATED',
        formula: 'HONORARIOS_AGENTE + GASTOS_PORTUARIOS',
        description: 'Total gastos de despacho aduanero',
        order: 18,
      },
      // Flete interno + lookup
      {
        code: 'TARIFA_FLETE_INTERNO_REF',
        name: 'Tarifa Flete Interno (consultar tabla)',
        variableType: 'LOOKUP',
        description: 'Consulta la tabla de flete interno según origen y destino',
        order: 19,
        lookupTableCode: 'TARIFAS_FLETE_INTERNO',
      },
      {
        code: 'FLETE_INTERNO_RATE',
        name: 'Tarifa Flete Interno $us/ton',
        variableType: 'CURRENCY',
        defaultValue: '380',
        description: 'Tarifa por tonelada (puede usar el valor de la tabla)',
        order: 20,
      },
      {
        code: 'FLETE_INTERNO',
        name: 'Flete Interno $us',
        variableType: 'CALCULATED',
        formula: 'FLETE_INTERNO_RATE * PESO_KG / 1000',
        description: 'Flete interno calculado por peso',
        order: 21,
      },
      // Almacenaje
      {
        code: 'DIAS_ALMACENAJE',
        name: 'Días de Almacenaje',
        variableType: 'NUMBER',
        defaultValue: '5',
        description: 'Días de almacenamiento en deposito',
        order: 22,
      },
      {
        code: 'TARIFA_ALMACENAJE',
        name: 'Tarifa Almacenaje $us/dia',
        variableType: 'CURRENCY',
        defaultValue: '15',
        description: 'Costo diario de almacenaje',
        order: 23,
      },
      {
        code: 'TOTAL_ALMACENAJE',
        name: 'Total Almacenaje $us',
        variableType: 'CALCULATED',
        formula: 'DIAS_ALMACENAJE * TARIFA_ALMACENAJE',
        description: 'Costo total de almacenaje',
        order: 24,
      },
      // Totales
      {
        code: 'COSTO_TOTAL_IMPORT',
        name: 'Costo Total Importación $us',
        variableType: 'CALCULATED',
        formula:
          'FLETE_INTL + SEGURO_MONTO + TOTAL_TRIBUTOS + TOTAL_DESPACHO + FLETE_INTERNO + TOTAL_ALMACENAJE',
        description: 'Suma de todos los costos de importación',
        order: 25,
      },
      {
        code: 'IMPORT_PCT_FOB',
        name: '% Costo Import. sobre FOB',
        variableType: 'CALCULATED',
        formula: 'VALOR_FOB > 0 ? COSTO_TOTAL_IMPORT / VALOR_FOB * 100 : 0',
        description: 'Porcentaje que representa la importación sobre el FOB',
        order: 26,
      },
      {
        code: 'VALOR_DDP',
        name: 'Valor DDP $us',
        variableType: 'CALCULATED',
        formula: 'VALOR_FOB + COSTO_TOTAL_IMPORT',
        description: 'Delivered Duty Paid = FOB + todos los costos',
        order: 27,
      },
      {
        code: 'COSTO_UNITARIO_IMPORT',
        name: 'Costo Import. por Unidad $us',
        variableType: 'CALCULATED',
        formula: 'CANTIDAD_BULTOS > 0 ? COSTO_TOTAL_IMPORT / CANTIDAD_BULTOS : COSTO_TOTAL_IMPORT',
        description: 'Costo de importación dividido entre bultos',
        order: 28,
      },
    ],
  },
];
